/**
 * ProcessViewElement — kreator formatek: klocek palety reprezentujący historię
 * procesu akceptacji rekordu (`process_view`, patrz `RecursiveFormRenderer`
 * PLACEHOLDER_TYPES). F17 kontynuacja (Commit B) — dotąd tabela historii akceptacji
 * była dostępna WYŁĄCZNIE jako seedowana zakładka `approvals` (slug z
 * `seed-system-modules.ts`, patrz `layout-shape-adapter.ts` typ `'approvals'`).
 * Ten klocek domyka rozjazd względem „Tabeli dodatkowej"/„Historii zmian" —
 * tenant stawia historię procesu TAM, GDZIE CHCE (w zakładce „Dane", w sekcji,
 * w akordeonie), a seedowana zakładka `approvals` zostaje jako alternatywa
 * (WYCOFANIE poza zakresem tego commita — osobny krok migracji).
 *
 * Mirror `AdditionalTableViewElement` — na REALNEJ stronie rekordu ten typ jest
 * dispatchowany WYŁĄCZNIE przez `contextRenderers['process_view']` (montuje
 * `ProcessHistoryTab`, patrz `document-additional-renderers.tsx` i lokalna mapa
 * w `ContractorDetail.tsx`). Ten plik obsługuje TYLKO podgląd w canvasie
 * buildera (`mode='builder'`) oraz panel właściwości (moduleCode/recordId płyną
 * z kontekstu strony, nie z `settings` klocka).
 *
 * Ustawienia klocka (oba domyślnie wyłączone): `showCancelled` („Pokaż anulowane sprawy")
 * i `showDecisionActions` („Pokaż przyciski decyzji" — na kartach historii przyciski
 * akceptanta i „Anuluj proces", jak w banerze nad zakładkami) — renderery na stronach
 * rekordów przekazują je do `ProcessHistoryTab` przez `parseProcessViewElementConfig`.
 * Brak wartości w zapisanym układzie = wyłączone; ustawienia nie są migrowane w danych.
 *
 * `allowedModules` — kartoteki, których strona detalu REALNIE dispatchuje
 * `process_view` przez `contextRenderers` (zweryfikowane grepem `processesActive`/
 * `ProcessApprovalBanner`): 7 klas dokumentów (przez `buildDocumentAdditionalRenderers`)
 * + `contractors` + moduły własne tenanta (`CUSTOM_MODULE_PALETTE_SCOPE` — kreator modułu
 * własnego; na stronie rekordu `RecordFormPage` montuje historię z kodem procesu
 * `custom.<slug>`). Employees/users mają dziś `additional_view`, ale NIE mają
 * jeszcze bannera/historii procesów na karcie — dodać do listy razem z wpięciem
 * renderera na danej stronie, nie wcześniej (ten sam wzorzec ograniczenia co
 * `AdditionalTableViewElement`).
 *
 * `icon: 'GitBranch'` — już ustalona ikona modułu Procesy w projekcie (sidebar
 * `config/menu.ts`, `tenant-tab-meta.ts` klucz `processes`), nie nowa propozycja.
 */

import { GitBranch } from 'lucide-react';

import { Checkbox } from '@/components/Checkbox';
import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import { CUSTOM_MODULE_PALETTE_SCOPE } from '@/components/modules/palette-scopes';
import i18n from '@/i18n';

export interface ProcessViewElementConfig {
  /** Czy historia pokazuje anulowane sprawy (status `cancelled`). Domyślnie `false`. */
  showCancelled: boolean;
  /**
   * Czy karty historii niosą przyciski decyzji i „Anuluj proces” (jak baner nad zakładkami).
   * Domyślnie `false`. Bez „Wyślij do akceptacji” — start sprawy to osobny element.
   */
  showDecisionActions: boolean;
}

/**
 * Defensywny odczyt `settings` klocka: każde ustawienie jest `true` WYŁĄCZNIE dla literalnego
 * boolean `true`; brak ustawienia / `false` / dowolna inna wartość → `false`.
 */
export function parseProcessViewElementConfig(
  settings: Record<string, unknown> | undefined,
): ProcessViewElementConfig {
  return {
    showCancelled: settings?.showCancelled === true,
    showDecisionActions: settings?.showDecisionActions === true,
  };
}

/**
 * Zakresy palety, na których stronie rekordu klocki procesowe mają realny render (patrz nagłówek
 * pliku) — wspólne dla „Procesu akceptacji” i „Przycisku akceptacji” (`ProcessActionButtonElement`).
 */
export const PROCESS_ELEMENT_ALLOWED_MODULES: readonly string[] = [
  'contractors',
  'purchase_invoices',
  'sale_invoices',
  'contracts',
  'letters',
  'commercial',
  'internal',
  'specjalne',
  CUSTOM_MODULE_PALETTE_SCOPE,
];

export const ProcessViewElement: LayoutElementDefinition = {
  type: 'process_view',
  category: 'tresc',
  isContainer: false,
  label: 'Proces akceptacji',
  icon: 'GitBranch',
  allowedModules: [...PROCESS_ELEMENT_ALLOWED_MODULES],
  defaultSettings: () => ({ showCancelled: false, showDecisionActions: false }),
  propertiesEditor: ({ settings, onChange }) => (
    <div className="space-y-3" data-testid="process-view-properties-editor">
      <Checkbox
        checked={parseProcessViewElementConfig(settings).showCancelled}
        onChange={(e) => onChange({ showCancelled: e.target.checked })}
        data-testid="process-view-prop-show-cancelled"
        label={i18n.t('tenant.processes.history.showCancelled', {
          defaultValue: 'Pokaż anulowane sprawy',
        })}
      />
      <Checkbox
        checked={parseProcessViewElementConfig(settings).showDecisionActions}
        onChange={(e) => onChange({ showDecisionActions: e.target.checked })}
        data-testid="process-view-prop-show-decision-actions"
        label={i18n.t('tenant.processes.history.showDecisionActions', {
          defaultValue: 'Pokaż przyciski decyzji',
        })}
      />
    </div>
  ),
  render: ({ element }) => (
    <div
      className="rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center"
      data-testid={`process-view-element-${element.id}`}
      data-element-type="process_view"
    >
      <GitBranch className="mx-auto h-5 w-5 text-text-muted" aria-hidden="true" />
      <p className="mt-1 text-xs font-medium text-text-secondary">
        {i18n.t('tenant.processes.history.paletteLabel', { defaultValue: 'Proces akceptacji' })}
      </p>
    </div>
  ),
};
